import { z } from 'zod';
import {
  encounterMeasurementInputCodes,
  encounterMeasurementRanges,
  type EncounterMeasurementCode,
  type EncounterStatus,
} from '../enums.js';
import { optionalText, trimmed } from './_fields.js';
import type {
  BodyDiagramSummaryDto,
  ClinicalTestResultDto,
  QuestionnaireResponseDto,
} from './encounters-clinical.js';

/** Le contenu clinique fait partie du même contrat : une seule entrée dans `dto/index.ts`. */
export * from './encounters-clinical.js';

/**
 * Contrat réseau de la consultation (`encounter`), partagé mot pour mot entre le serveur et le
 * formulaire en huit sections. Trois principes tenus par ces schémas :
 * — un brouillon est une consultation en `draft`, pas une table d'autosauvegarde parallèle ;
 * — tout ce qui est structuré (mesures, tests, questionnaires, schémas) a sa propre ressource,
 *   donc aucun jsonb fourre-tout ne transite ici ;
 * — le client n'envoie jamais une valeur calculée (IMC, score) : le serveur seul les produit.
 */

/* -------------------------------------------------------------------------- */
/* Entrées                                                                     */
/* -------------------------------------------------------------------------- */

export const encounterIdParamSchema = z.object({ id: z.uuid() }).strict();

/** Consultation déjà liée à un rendez-vous, lue pour rouvrir plutôt que recréer. */
export const encounterAppointmentParamSchema = z
  .object({ appointmentId: z.uuid() })
  .strict();

export const encounterListQuerySchema = z
  .object({
    patientId: z.uuid(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(20),
  })
  .strict();
export type EncounterListQuery = z.infer<typeof encounterListQuerySchema>;

/**
 * Création d'un brouillon. Le praticien n'est pas dans le corps : c'est l'appelant, résolu depuis
 * la session. `startedAt` absent vaut maintenant ; le formulaire peut antidater une saisie.
 */
export const encounterCreateSchema = z
  .object({
    patientId: z.uuid(),
    appointmentId: z.uuid().nullable().optional(),
    consultationTypeId: z.uuid().nullable().optional(),
    consultationReasonId: z.uuid().nullable().optional(),
    locationId: z.uuid().nullable().optional(),
    startedAt: z.iso.datetime().optional(),
    title: optionalText(200).optional(),
  })
  .strict();
export type EncounterCreateInput = z.infer<typeof encounterCreateSchema>;

/**
 * Cible de l'enregistrement automatique : partiel, accepté en `draft` comme en `completed`.
 * Le patient n'y figure pas — une consultation ne change pas de dossier (la V1 le dépouillait
 * silencieusement côté serveur, ce qui revenait au même sans le dire).
 */
export const encounterUpdateSchema = z
  .object({
    title: optionalText(200).optional(),
    anamnesis: optionalText(20_000).optional(),
    observations: optionalText(20_000).optional(),
    diagnosticEvolution: optionalText(20_000).optional(),
    treatment: optionalText(20_000).optional(),
    conclusion: optionalText(20_000).optional(),
    /** Conseils donnés au patient. Distinct des notes de conclusion : ce n'est pas le même récit. */
    advice: optionalText(20_000).optional(),
    /** Commentaire libre du praticien sur son examen ostéopathique (section « Tests & traitement »). */
    osteopathicTests: optionalText(20_000).optional(),
    locationId: z.uuid().nullable().optional(),
    consultationTypeId: z.uuid().nullable().optional(),
    consultationReasonId: z.uuid().nullable().optional(),
    appointmentId: z.uuid().nullable().optional(),
    startedAt: z.iso.datetime().optional(),
  })
  .strict();
export type EncounterUpdateInput = z.infer<typeof encounterUpdateSchema>;

/**
 * Remplacement de l'ensemble des mesures. `bmi` n'est pas un code acceptable en entrée : il est
 * recalculé à partir de la taille et du poids, comme l'affichage en lecture seule de la V1.
 * Chaque code porte sa plage de vraisemblance, une seule fois, dans `encounterMeasurementRanges`.
 */
export const encounterMeasurementSchema = z
  .object({
    code: z.enum(encounterMeasurementInputCodes),
    value: z.number(),
  })
  .strict()
  .superRefine((entry, ctx) => {
    const range = encounterMeasurementRanges[entry.code];
    if (entry.value < range.min || entry.value > range.max) {
      ctx.addIssue({
        code: 'custom',
        path: ['value'],
        message: `Valeur attendue entre ${String(range.min)} et ${String(range.max)}`,
      });
    }
    if (range.integer === true && !Number.isInteger(entry.value)) {
      ctx.addIssue({ code: 'custom', path: ['value'], message: 'Valeur entière attendue' });
    }
  });

export const encounterMeasurementsPutSchema = z
  .object({
    measurements: z.array(encounterMeasurementSchema).max(encounterMeasurementInputCodes.length),
  })
  .strict();
export type EncounterMeasurementsPutInput = z.infer<typeof encounterMeasurementsPutSchema>;

/* -------------------------------------------------------------------------- */
/* Sorties                                                                     */
/* -------------------------------------------------------------------------- */

export interface EncounterMeasurementDto {
  code: EncounterMeasurementCode;
  value: number;
}

/** Ligne de liste : ce qu'affiche la chronologie du dossier patient, sans le contenu narratif. */
export interface EncounterSummaryDto {
  id: string;
  patientId: string;
  practitionerMemberId: string;
  appointmentId: string | null;
  locationId: string | null;
  consultationTypeId: string | null;
  consultationReasonId: string | null;
  status: EncounterStatus;
  startedAt: string;
  completedAt: string | null;
  title: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Ligne de la chronologie du dossier : le résumé, plus l'état du suivi patient de la séance.
 * Ni question ni réponse — l'étape Suivi de la consultation les montre.
 */
export interface EncounterListItemDto extends EncounterSummaryDto {
  /** Réponse la plus récente du patient à un suivi de cette séance ; `null` sans réponse. */
  followUpAnsweredAt: string | null;
  /** Un suivi programmé ou envoyé attend encore sa réponse. */
  followUpPending: boolean;
}

export interface EncounterDto extends EncounterSummaryDto {
  anamnesis: string | null;
  observations: string | null;
  diagnosticEvolution: string | null;
  /** Commentaire libre sur l'examen ostéopathique ; distinct des résultats de tests catalogués. */
  osteopathicTests: string | null;
  treatment: string | null;
  conclusion: string | null;
  /** Conseils donnés au patient. `null` quand le champ est vide : une seule forme du vide. */
  advice: string | null;
  createdBy: string | null;
}

/** Rendez-vous lié, réduit à ce que le formulaire affiche : aucune note, aucune donnée patient. */
export interface EncounterAppointmentLinkDto {
  id: string;
  startsAt: string;
  endsAt: string;
  status: string;
}

export interface EncounterDetailDto extends EncounterDto {
  measurements: EncounterMeasurementDto[];
  clinicalTests: ClinicalTestResultDto[];
  questionnaires: QuestionnaireResponseDto[];
  bodyDiagrams: BodyDiagramSummaryDto[];
  appointment: EncounterAppointmentLinkDto | null;
  /**
   * Un bilan pédiatrique est enregistré. La section suit l'âge à la date de la séance, mais un
   * bilan déjà saisi reste accessible même si une date corrigée depuis fait passer l'enfant
   * au-delà d'un an.
   */
  hasPediatricAssessment: boolean;
}

/* -------------------------------------------------------------------------- */
/* Modèles de questionnaire — paramètres de route                              */
/* -------------------------------------------------------------------------- */

export const questionnaireTemplateIdParamSchema = z.object({ id: z.uuid() }).strict();

export const questionnaireTemplateListQuerySchema = z
  .object({
    anatomicalZone: trimmed(64).optional(),
    includeInactive: z
      .enum(['true', 'false'])
      .default('false')
      .transform((v) => v === 'true'),
  })
  .strict();
export type QuestionnaireTemplateListQuery = z.infer<typeof questionnaireTemplateListQuerySchema>;
